import { useTranslation } from 'next-i18next'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { IProductCard } from '@src/components/product-card/product-card.types'
import BATCHED_EVENT_LIMIT from '@src/constants/batchedEventLimit'
import { Loader, LoaderWrapper } from '@src/quince-components/src'
import { getSelectedColorFromSelectedOptions } from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import InfiniteScrollProductList from '@src/quince-components/src/molecules/productListSectionTwo/infiniteScrollProductList'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import QuinceError from '@src/quince-site-common/src/domains/QuinceError'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import useFetch from '@src/quince-site-common/src/hooks/useFetch'
import useIntersect from '@src/quince-site-common/src/hooks/useIntersect'
import { useTrackIsElementVisible } from '@src/quince-site-common/src/hooks/useTrackIsElementVisible'
import pdpStateManager from '@src/quince-site-common/src/redux/globalStateManager/PDPStateManager'
import { useAppSelector } from '@src/quince-site-common/src/redux/hooks'
import { batchedAnalytics } from '@src/quince-site-common/src/utils/batched-analytics'
import { MAX_NUMBER_OF_COLOR_OPTIONS } from '@src/quince-site-common/src/utils/constants'
import getMaxWidthInPx from '@src/quince-site-common/src/utils/getMaxWidthInPx'
import { getProductDataForBatchedAnalytics } from '@src/quince-site-common/src/utils/getProductDataForBatchedAnalytics'
import { getProductTagsDataForBatchedAnalytics } from '@src/quince-site-common/src/utils/getProductTagsDataForBatchedAnalytics'
import { RECOMMENDED_SECTION_IDS } from '@src/quince-site-pdp/src/contexts/productPageContext.constants'
import BundlePageSectionProps from '@src/quince-site-pdp/src/domains/BundlePageSectionProps'
import { FINDING_METHOD_AGGREGATE } from '@src/telemetry/findingMethod'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import { productContextInitialState } from '../../../contexts/ProductPageContext'
import ProductPageSectionProps from '../../../domains/ProductPageSectionProps'
import useProductPageContext from '../../../hooks/useProductContext'
import { getSelectedColor, RECOMMENDED_PRODUCTS_TYPE } from '../utils'

import { getBoytProductsApi, IGetBoytProductsApiParams } from './boyt.api'
import { getTrackingMetaData } from './youMayAlsoLike.constants'
import styles from './youMayAlsoLike.module.scss'

const ERROR_NAME = 'Based on your Taste'
const BOYT_API_ERROR = 'Failed while fetching next product in product service getNextFeedForBYOT()'

const YouMayAlsoLike = (props: ProductPageSectionProps | BundlePageSectionProps) => {
  const { t } = useTranslation('common')
  const {
    recommendationUIData: { [RECOMMENDED_SECTION_IDS.SLOT4]: filteredYouMayAlsoLikeProducts },
    updateRecommendationAPIData,
    isRecommendationLoading,
    newPDPRecommendations,
  } = useProductPageContext()

  const product = 'bundle' in props ? props.bundle : props.product

  const deviceInfo = useDeviceInfo()

  const [nextUrl, setNextUrl] = useState<string | undefined>(undefined)
  const [isNextBatchLoading, setIsNextBatchLoading] = useState(false)
  const [intersectRef, isIntersecting] = useIntersect(null, '200px', 0, false)

  const selectedOptions = useAppSelector<ProductVariantOption[]>(pdpStateManager, pdpStateManager.getSelectedOptions)
  const selectedColor = getSelectedColor(selectedOptions)
  const productId = useAppSelector<string | undefined>(pdpStateManager, pdpStateManager.getProductIdFromRedux)
  const { fetchData: fetchRecommendedProducts, isLoading } = useFetch(getBoytProductsApi, {})

  const recommendedProductApiReqParams: IGetBoytProductsApiParams = {
    product,
    isNewPDPRecommendations: newPDPRecommendations.isInBucket,
    selectedOptions,
    recommendedProductsType: RECOMMENDED_PRODUCTS_TYPE.BOYT,
    nextUrl,
  }

  useEffect(() => {
    // selected color is a required parameter for fetchRecommendedProducts
    if (selectedColor && productId === product.id.toString() && newPDPRecommendations.isInitialized) {
      const fetchData = () => {
        fetchRecommendedProducts(recommendedProductApiReqParams)
          .then(res => {
            const { data = [], nextUrl } = res ?? {}
            setNextUrl(nextUrl)
            updateRecommendationAPIData(RECOMMENDED_SECTION_IDS.SLOT4, {
              ...productContextInitialState.recommendationUIData[RECOMMENDED_SECTION_IDS.SLOT4],
              data: data as unknown as IProductCard[],
            })
          })
          .catch((err: unknown) => {
            const error = new Error(BOYT_API_ERROR)
            error.name = ERROR_NAME
            QuinceError.captureDataException(
              error,
              err as Error,
              undefined,
              MODULES.RECOMMENDATION,
              FEATURES.BASED_ON_YOUR_TASTE,
            )
          })
      }
      fetchData()
    }
  }, [selectedColor, productId, newPDPRecommendations.isInitialized])

  const isLoadedWithData = !isRecommendationLoading && !isLoading && filteredYouMayAlsoLikeProducts.data.length

  useEffect(() => {
    if (isIntersecting && nextUrl && !filteredYouMayAlsoLikeProducts.has_reached_limit) {
      setIsNextBatchLoading(true)
      fetchRecommendedProducts(recommendedProductApiReqParams)
        .then(({ data, nextUrl }) => {
          setNextUrl(nextUrl)
          setIsNextBatchLoading(false)
          updateRecommendationAPIData(RECOMMENDED_SECTION_IDS.SLOT4, {
            ...productContextInitialState.recommendationUIData[RECOMMENDED_SECTION_IDS.SLOT4],
            data: [...filteredYouMayAlsoLikeProducts.data, ...data],
          })
        })
        .catch((err: unknown) => {
          setIsNextBatchLoading(false)
          const error = new Error(BOYT_API_ERROR)
          QuinceError.captureDataException(
            error,
            err as Error,
            undefined,
            MODULES.RECOMMENDATION,
            FEATURES.BASED_ON_YOUR_TASTE,
          )
        })
    }
  }, [isIntersecting])

  const displayTitle = useMemo(() => filteredYouMayAlsoLikeProducts.module_title, [filteredYouMayAlsoLikeProducts])

  const getIsVisibleTrackParams = useCallback(() => {
    const productId = product.id

    return {
      // @i18n-ignore
      action: 'you_may_also_like',
      value: true,
      context: { productId, selectedColor, ...getTrackingMetaData(displayTitle).context },
    }
  }, [product.id, selectedColor, displayTitle])

  const getRecommendationVisibleTrackParams = useCallback(() => {
    trackEvent(TELEMETRY_EVENT_NAMES.RECOMMENDATION_VIEW, filteredYouMayAlsoLikeProducts)
  }, [product.id, filteredYouMayAlsoLikeProducts])

  const { ref: trackIsVisibleRef } = useTrackIsElementVisible(
    getIsVisibleTrackParams,
    getRecommendationVisibleTrackParams,
  )

  const maxWidth = getMaxWidthInPx({
    maxWidthMobileInRem: styles.productCardWidthMobile ?? '',
    maxWidthDesktopInRem: styles.productCardWidth ?? '',
    deviceType: deviceInfo.deviceType,
  })

  // @i18n-ignore
  const productViewTracker = `${product.slug}_${TELEMETRY_EVENT_NAMES.BULK_PRODUCT_CARD_VIEW}_based_on_your_taste`
  useEffect(() => {
    if (productViewTracker) {
      batchedAnalytics.initializeBatchedAnalyticsEvent({
        tracker: productViewTracker,
        eventLimit: BATCHED_EVENT_LIMIT[TELEMETRY_EVENT_NAMES.BULK_PRODUCT_CARD_VIEW],
        debouncePeriod: 1000,
      })
      batchedAnalytics.trackBatchedAnalytics({
        id: `internalAnalytics-${productViewTracker}`,
        tracker: productViewTracker,
        callback: ({ events }) => {
          const productList = events.map(event => getProductDataForBatchedAnalytics(event))
          const tagsCount = getProductTagsDataForBatchedAnalytics(productList)
          trackEvent(TELEMETRY_EVENT_NAMES.BULK_PRODUCT_CARD_VIEW, {
            productList: productList,
            tagsCount: tagsCount,
            ...getTrackingMetaData(displayTitle).context,
          })
        },
      })
    }
  }, [])

  if (
    !nextUrl &&
    ((Array.isArray(filteredYouMayAlsoLikeProducts.data) && filteredYouMayAlsoLikeProducts.data.length === 0) ||
      (Array.isArray(filteredYouMayAlsoLikeProducts) && filteredYouMayAlsoLikeProducts.length === 0))
  ) {
    return null
  }

  console.log('isloading, isRecommendationLoading', isLoading, isRecommendationLoading)
  return (
    <div ref={isLoadedWithData ? trackIsVisibleRef : undefined} className={styles.youMayAlsoLike}>
      <h2 className={styles.youMayAlsoLike__title}>{t(filteredYouMayAlsoLikeProducts.module_title_key)}</h2>
      <LoaderWrapper showLoader={isRecommendationLoading}>
        <>
          {/* {isLoading && null} */}
          {/* {!isLoading && !isRecommendationLoading && ( */}
          {!isRecommendationLoading && (
            <>
              <InfiniteScrollProductList
                productViewTracker={productViewTracker}
                productCards={filteredYouMayAlsoLikeProducts.data}
                swiperClassName={styles.infiniteScrollSection__wrapper}
                gridColCount={deviceInfo.deviceType === DEVICE_TYPES.MOBILE ? 2 : 4}
                // @i18n-ignore
                tracker="product_page_you_may_also_like"
                productCardDesktopClassName={
                  deviceInfo.deviceType === DEVICE_TYPES.DESKTOP ? styles.infiniteScrollSection__desktopProductCard : ''
                }
                productCardMobileClassName={
                  deviceInfo.deviceType === DEVICE_TYPES.MOBILE ? styles.infiniteScrollSection__mobileProductCard : ''
                }
                maxWidth={maxWidth}
                productClickTrackConfig={{
                  trackInternal: true,
                  // @i18n-ignore
                  category: 'you_may_also_like',
                  // @i18n-ignore
                  action: 'click_you_may_also_like',
                  context: {
                    source_product_id: product.shopifyProductId,
                    source_product_color: getSelectedColorFromSelectedOptions(selectedOptions),
                    ...getTrackingMetaData(displayTitle).context,
                    findingMethod: FINDING_METHOD_AGGREGATE.RECOMMENDATION,
                  },
                }}
                optionLimit={MAX_NUMBER_OF_COLOR_OPTIONS}
                skipTagRefresh
              />
              <div ref={intersectRef}></div>
              <Loader showLoader={isLoading || isNextBatchLoading} />
            </>
          )}
        </>
      </LoaderWrapper>
    </div>
  )
}

export default YouMayAlsoLike
